<template>
  <header class="site-header">
    <!-- 顶部信息栏 -->
    <div class="header-top">
      <div class="container">
        <div class="top-left">
          <span class="welcome-text">{{ getWelcomeText }}</span>
        </div>
        <div class="top-right">
          <el-dropdown trigger="click" @command="handleLanguageChange">
            <span class="lang-selector">
              <el-icon><Promotion /></el-icon>
              {{ currentLangName }}
              <el-icon><ArrowDown /></el-icon>
            </span>
            <template #dropdown>
              <el-dropdown-menu class="lang-menu">
                <el-dropdown-item v-for="lang in languages" :key="lang.code" :command="lang.code">
                  <span class="lang-option">
                    <span class="lang-flag">{{ lang.flag }}</span>
                    {{ lang.name }}
                  </span>
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>
    </div>
    <!-- 主头部 -->
    <div class="header-main">
      <div class="container">
        <div class="header-content">
          <!-- Logo -->
          <router-link to="/" class="logo">
            <div v-if="siteLogo" class="logo-image">
              <img :src="siteLogo" :alt="siteName" />
            </div>
            <div v-else class="logo-icon">
              <el-icon :size="32"><Box /></el-icon>
            </div>
            <div class="logo-text">
              <span class="logo-name">{{ siteName }}</span>
              <span class="logo-slogan">{{ siteSlogan }}</span>
            </div>
          </router-link>
          <!-- 搜索框 -->
          <div class="header-search">
            <el-input
              v-model="searchKeyword"
              :placeholder="$t('header.searchPlaceholder')"
              size="large"
              @keyup.enter="handleSearch"
            >
              <template #append>
                <el-button @click="handleSearch">
                  <el-icon><Search /></el-icon>
                </el-button>
              </template>
            </el-input>
          </div>
          <!-- 联系按钮 -->
          <div class="header-actions">
            <el-button type="primary" @click="$router.push('/contact')">
              <el-icon><Message /></el-icon>
              {{ $t('header.contactSupplier') }}
            </el-button>
          </div>
        </div>
      </div>
    </div>
    <!-- 公司信息栏 -->
    <div class="company-bar">
      <div class="container">
        <div class="company-info">
          <div class="company-name">{{ companyName }}</div>
          <div class="company-badges">
            <el-tag type="success" size="small">{{ getBadgeVerified }}</el-tag>
            <el-tag size="small">{{ getBadgeCustom }}</el-tag>
            <el-tag size="small">{{ badgeYears }}</el-tag>
          </div>
        </div>
        <div class="company-trust">
          <span class="trust-badge">
            <el-icon><CircleCheck /></el-icon>
            {{ getTrustOem }}
          </span>
          <span class="trust-badge">
            <el-icon><CircleCheck /></el-icon>
            {{ getTrustFortune }}
          </span>
          <span class="trust-badge">
            <el-icon><CircleCheck /></el-icon>
            {{ getTrustQuality }}
          </span>
        </div>
      </div>
    </div>
    <!-- 主导航菜单 -->
    <nav class="main-nav">
      <div class="container">
        <div class="nav-content">
          <!-- 主导航菜单 -->
          <ul class="nav-menu">
            <li v-for="item in menuItems" :key="item.path" class="nav-item">
              <router-link :to="item.path" class="nav-link" active-class="active">
                {{ $t(item.nameKey) }}
              </router-link>
            </li>
          </ul>
          <!-- 快捷链接 -->
          <div class="nav-shortcuts">
            <router-link to="/products" class="shortcut-item">
              <el-icon><Goods /></el-icon>
              <span>{{ $t('nav.products') }}</span>
            </router-link>
            <router-link to="/about" class="shortcut-item">
              <el-icon><OfficeBuilding /></el-icon>
              <span>{{ $t('nav.about') }}</span>
            </router-link>
            <router-link to="/contact" class="shortcut-item">
              <el-icon><Phone /></el-icon>
              <span>{{ $t('nav.contact') }}</span>
            </router-link>
          </div>
        </div>
      </div>
    </nav>
  </header>
</template>

<script setup>
import { ref, computed, reactive, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Search, Message, Box, Promotion, ArrowDown, CircleCheck, Goods, OfficeBuilding, Phone } from '@element-plus/icons-vue'
import { useConfig } from '@/composables/useConfig'
import { getFullImageUrl } from '@/config/app'

const router = useRouter()
const { locale } = useI18n()
const searchKeyword = ref('')

// 从后端配置获取网站信息
const { getConfig, loading: configLoading } = useConfig()
const siteName = computed(() => getConfig('site.name', ''))
const siteLogo = computed(() => {
  const logo = getConfig('site.logo', '')
  return getFullImageUrl(logo)
})
const siteSlogan = computed(() => getConfig('site.slogan', ''))
const welcomeText = computed(() => getConfig('header.welcome', ''))
// 根据当前语言显示对应的公司名称
const companyName = computed(() => {
  // 使用增强的 getConfig 函数，它会自动根据当前语言返回适当的值
  return getConfig('company.name', '')
})

// 认证徽章
const badgeVerified = computed(() => getConfig('company.badge.verified', ''))
const badgeCustom = computed(() => getConfig('company.badge.custom', ''))

// 成立年限（从 company.years 获取，动态生成显示文本）
const badgeYears = computed(() => {
  const years = getConfig('company.years', '')
  return years ? `${years}` : ''
})

// 信任标识
const trustOem = computed(() => getConfig('company.trust.oem', '知名品牌OEM'))
const trustFortune = computed(() => getConfig('company.trust.fortune', '世界500强供应商'))
const trustQuality = computed(() => getConfig('company.trust.quality', '质量管理体系认证'))

// 计算属性，返回多语言配置的文本
const getWelcomeText = computed(() => {
  return welcomeText.value
})

const getBadgeVerified = computed(() => {
  return badgeVerified.value
})

const getBadgeCustom = computed(() => {
  return badgeCustom.value
})

const getTrustOem = computed(() => {
  return trustOem.value
})

const getTrustFortune = computed(() => {
  return trustFortune.value
})

const getTrustQuality = computed(() => {
  return trustQuality.value
})

const menuItems = reactive([
  { nameKey: 'nav.home', path: '/' },
  { nameKey: 'nav.products', path: '/products' },
  { nameKey: 'nav.about', path: '/about' },
  { nameKey: 'nav.contact', path: '/contact' }
])

const languages = [
  { code: 'zh-CN', name: '简体中文', flag: 'CN' },
  { code: 'en-US', name: 'English', flag: 'US' },
  { code: 'de-DE', name: 'Deutsch', flag: 'DE' },
  { code: 'pt-PT', name: 'Português', flag: 'PT' },
  { code: 'es-ES', name: 'Español', flag: 'ES' },
  { code: 'fr-FR', name: 'Français', flag: 'FR' },
  { code: 'it-IT', name: 'Italiano', flag: 'IT' },
  { code: 'ru-RU', name: 'Русский', flag: 'RU' },
  { code: 'ko-KR', name: '한국어', flag: 'KR' },
  { code: 'ja-JP', name: '日本語', flag: 'JP' },
  { code: 'ar-SA', name: 'العربية', flag: 'SA' },
  { code: 'th-TH', name: 'ภาษาไทย', flag: 'TH' },
  { code: 'vi-VN', name: 'Tiếng Việt', flag: 'VN' },
  { code: 'tr-TR', name: 'Türk', flag: 'TR' },
  { code: 'nl-NL', name: 'Nederlands', flag: 'NL' },
  { code: 'id-ID', name: 'Indonesian', flag: 'ID' },
  { code: 'he-IL', name: 'עברית', flag: 'IL' }
]

// 初始化语言 - 从本地存储读取用户偏好
const savedLanguage = localStorage.getItem('preferred-language')
if (savedLanguage && locale.value !== savedLanguage) {
  locale.value = savedLanguage
}

// 监听语言变化事件
window.addEventListener('languageChanged', (event) => {
  // 当语言改变时，可能需要触发一些特定的更新
  console.log('Language changed to:', event.detail.newLang)
})

// 监听语言变化，重新加载翻译
watch(locale, async () => {
  // 语言变化时，配置会自动更新，不需要手动重新加载
})

// 监听配置加载完成事件
watch(configLoading, async (loading) => {
  if (!loading) {
    // 配置加载完成后，数据会自动更新
  }
})

const currentLangName = computed(() => {
  const lang = languages.find(l => l.code === locale.value)
  return lang ? lang.name : '简体中文'
})

const handleLanguageChange = (lang) => {
  // 更新 i18n 语言
  locale.value = lang
  
  // 保存用户选择的语言到 localStorage
  localStorage.setItem('preferred-language', lang)
  
  // 触发全局语言变化事件，通知所有组件更新
  window.dispatchEvent(new CustomEvent('languageChanged', { detail: { newLang: lang } }))
  
  // 为了确保界面完全更新，可以强制路由刷新或页面局部更新
  // 这里使用一个简单的方法：延迟一小段时间后再继续，让 Vue 有时间响应变化
}

const handleSearch = () => {
  if (searchKeyword.value.trim()) {
    router.push({ path: '/products', query: { keyword: searchKeyword.value.trim() } })
  }
}
</script>

<style scoped>
.site-header {
  position: relative;
  z-index: 1000;
  background: var(--color-white);
  box-shadow: var(--shadow-sm);
}

.header-top {
  background-color: var(--color-bg-dark);
  color: var(--color-white);
  padding: 6px 0;
  font-size: 12px;
}

.header-top .container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.lang-selector {
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  color: #ccc;
}

.lang-selector:hover {
  color: var(--color-white);
}

.header-main {
  padding: 16px 0;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 32px;
}

.logo {
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  flex-shrink: 0;
}

.logo-icon {
  width: 48px;
  height: 48px;
  background-color: var(--color-primary);
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-white);
}

.logo-image {
  width: 48px;
  height: 48px;
  border-radius: var(--radius-md);
  overflow: hidden;
  flex-shrink: 0;
}

.logo-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.logo-text {
  display: flex;
  flex-direction: column;
}

.logo-name {
  font-size: 20px;
  font-weight: 700;
  color: var(--color-text-primary);
  letter-spacing: 1px;
}

.logo-slogan {
  font-size: 11px;
  color: var(--color-text-light);
}

.header-search {
  flex: 1;
  max-width: 500px;
}

.header-actions {
  flex-shrink: 0;
}

.company-bar {
  background-color: var(--color-bg-light);
  padding: 10px 0;
  border-top: 1px solid var(--color-border);
}

.company-bar .container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.company-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.company-name {
  font-size: 15px;
  font-weight: 600;
  color: var(--color-text-primary);
}

.company-badges {
  display: flex;
  gap: 8px;
}

.company-trust {
  display: flex;
  gap: 16px;
}

.trust-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--color-primary);
}

/* 主导航菜单 */
.main-nav {
  background-color: var(--color-bg-dark);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.nav-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: var(--nav-height);
}

.nav-menu {
  display: flex;
  gap: 0;
}

.nav-item {
  list-style: none;
}

.nav-link {
  display: flex;
  align-items: center;
  padding: 0 24px;
  height: var(--nav-height);
  color: #ccc;
  font-size: 15px;
  font-weight: 500;
  transition: all 0.3s ease;
  border-bottom: 3px solid transparent;
}

.nav-link:hover,
.nav-link.active {
  color: var(--color-white);
  border-bottom-color: var(--color-primary);
  background-color: rgba(255, 255, 255, 0.05);
}

.nav-shortcuts {
  display: flex;
  gap: 20px;
}

.shortcut-item {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #aaa;
  font-size: 13px;
  transition: color 0.3s ease;
}

.shortcut-item:hover {
  color: var(--color-white);
}

.lang-option {
  display: flex;
  align-items: center;
  gap: 8px;
}

.lang-flag {
  display: inline-block;
  width: 24px;
  height: 16px;
  background-color: var(--color-primary);
  color: var(--color-white);
  font-size: 10px;
  font-weight: 600;
  text-align: center;
  line-height: 16px;
  border-radius: 2px;
  flex-shrink: 0;
}

@media (max-width: 768px) {
  .header-content {
    flex-wrap: wrap;
  }
  .header-search {
    order: 3;
    max-width: 100%;
    width: 100%;
  }
  .company-trust {
    display: none;
  }
  .nav-shortcuts {
    display: none;
  }
  .nav-link {
    padding: 0 16px;
    font-size: 14px;
  }
}
</style>